import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { usePermissions } from "../context/PermissionContext";
import { apiCall } from "../Apis/apiCall";
import { useGeolocated } from "react-geolocated";
import { pointInPolygon } from "../utils/PointInPolygon";
import { useLocation } from "react-router-dom"; // Add this import

const AttendanceToggle = ({ messageShow = { check: false } }) => {
  const {
    isAuthenticated,
    isLoading,
    isAttendanceMarked,
    setIsAttendanceMarked,
    assignedLocations,
    fetchAttendanceStatus,
  } = usePermissions();
  const [isToggleChecked, setIsToggleChecked] = useState(isAttendanceMarked);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  const location = useLocation(); // Add this hook to get current route

  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
    },
    userDecisionTimeout: 10000,
  });

  // Check if current route is '/workflows-running'
  const isWorkflowsRunningRoute = location.pathname === '/workflows-running';

  // Sync local state with context
  useEffect(() => {
    setIsToggleChecked(isAttendanceMarked);
  }, [isAttendanceMarked]);

  // Determine valid location_id based on current coordinates
  const getValidLocationId = () => {
    if (!coords?.latitude || !coords?.longitude) {
      return null;
    }

    const userPoint = { lat: coords.latitude, lng: coords.longitude };
    const validLocation = assignedLocations.find((location) =>
      pointInPolygon(userPoint, location.polygons)
    );

    return validLocation ? validLocation.location_id : null;
  };

  // Handle toggle action (mark/unmark attendance)
  const handleToggleAttendance = async () => {
    setIsToggleLoading(true);

    try {
      // Re-fetch the latest attendance status and assigned locations before action
      await fetchAttendanceStatus();

      // Check geolocation availability and permissions
      if (!isGeolocationAvailable) {
        toast.error("Location not supported by this browser.");
        setIsToggleLoading(false);
        return;
      }

      if (!isGeolocationEnabled) {
        toast.error("Location is disabled. Please enable location services.");
        setIsToggleLoading(false);
        return;
      }

      if (!coords?.latitude || !coords?.longitude) {
        toast.error("Location data unavailable. Please try again.");
        setIsToggleLoading(false);
        return;
      }

      const locationId = getValidLocationId();
      if (!locationId) {
        toast.error("You are not within an assigned location area.");
        setIsToggleLoading(false);
        return;
      }

      const action = isToggleChecked ? "checkOut" : "checkIn";
      const payload = {
        location_id: locationId,
        latitude: coords.latitude,
        longitude: coords.longitude,
        action,
      };

      const response = await apiCall("post", "/staff-attendance", payload);
      const res = response?.data;
      if (!res.success) {
        throw new Error(res.message);
      }

      const newAttendanceStatus = !isToggleChecked;
      setIsToggleChecked(newAttendanceStatus);
      setIsAttendanceMarked(newAttendanceStatus);
      toast.success(`${res.message}`);
    } catch (error) {
      toast.error(error.message || "Failed to update attendance status.");
    } finally {
      setIsToggleLoading(false);
    }
  };

  // Log current coordinates for debugging
  useEffect(() => {
    if (coords) {
    }
  }, [coords]);

  return (
    <div className="attendance-toggle d-flex align-items-center">
      <style>
        {`
          .switch {
            position: relative;
            display: inline-block;
            width: 40px;
            height: 20px;
          }
          .switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: var(--neutral-300);
            transition: 0.4s;
            border-radius: 20px;
            background-color: var(--danger-600);
          }
          .slider:before {
            position: absolute;
            content: "";
            height: 16px;
            width: 16px;
            left: 2px;
            bottom: 2px;
            background-color: var(--white);
            transition: 0.4s;
            border-radius: 50%;
          }
          input:checked + .slider {
            background-color: var(--success-600);
          }
          input:checked + .slider:before {
            transform: translateX(20px);
          }
          input:disabled + .slider {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .slider.round {
            border-radius: 20px;
          }
          .slider.round:before {
            border-radius: 50%;
          }
          .attendance-toggle .text-sm {
            margin-left: 8px;
            font-size: var(--font-sm);
            color: var(--text-secondary-light);
          }
        `}
      </style>
      <label className="switch">
        <input
          type="checkbox"
          checked={isToggleChecked}
          onChange={handleToggleAttendance}
          disabled={isToggleLoading || !isAuthenticated || isWorkflowsRunningRoute} // Add route condition here
          aria-label={isToggleChecked ? "Unmark Attendance" : "Mark Attendance"}
        />
        <span className="slider round"></span>
      </label>
      {messageShow.check && (
        <span className={`text-sm`}>
          {isToggleLoading
            ? "Please wait..."
            : isWorkflowsRunningRoute // Add route condition here
            ? "Workflow Running"
            : !isGeolocationAvailable
            ? "Location Error"
            : !isGeolocationEnabled
            ? "Location Disabled"
            : !coords?.latitude || !coords?.longitude
            ? "Fetching Location..."
            : !getValidLocationId()
            ? "Not in an assigned area"
            : isToggleChecked
            ? "Online"
            : "Offline"}
        </span>
      )}
    </div>
  );
};

export default AttendanceToggle;